import React, { useState } from "react";
import { Button, Typography, Container, Card, CardContent, Box, Paper, FormControlLabel, Checkbox, Link } from "@mui/material";
import styled from "@emotion/styled";
import { useBrandName } from "../utils/brandUtils";

const StyledContainer = styled(Container)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    minHeight: "100vh",
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(0),
    },
}));

const WideStyledCard = styled(Card)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    [theme.breakpoints.up("sm")]: {
        width: "750px",
        margin: "0 auto",
    },
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(3),
    },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3),
}));

const StyledCopyright = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

const AgreementContainer = styled(Paper)(({ theme }) => ({
    maxHeight: "400px",
    overflow: "auto",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.surface.inset,
    border: `1px solid ${theme.palette.divider}`,
}));

const NavigationButtons = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
    width: "100%",
}));

const AgreementText = styled(Typography)(({ theme }) => ({
    whiteSpace: "pre-wrap",
    lineHeight: 1.6,
}));

const Copyright = () => {
    const brandName = useBrandName();

    return (
        <StyledCopyright variant="body2" color="textSecondary" align="center">
            {"Copyright © "}
            <Link color="inherit" href="https://pollux-mui.com/">
                {brandName}
            </Link>{" "}
            {new Date().getFullYear()}
            {"."}
        </StyledCopyright>
    );
};

const RegisterStep3 = ({ newRegistrationStep2, handleRegister, clientName }) => {
    const [hasAgreed, setHasAgreed] = useState(false);

    const agreementText = `
TERMS OF SERVICE

This Terms of Service agreement is made between CreativeWorks Systems, Inc ("CreativeWorks") and ${clientName || "the Client"}.

1. DESCRIPTION OF SERVICES:
${clientName || "Client"} hereby engages CreativeWorks to perform graphic design, web development, programming, internet marketing, and other digital services based on materials and requirements supplied by ${clientName || "Client"} from time to time. ${clientName || "Client"} has no obligation to utilize the services of CreativeWorks. CreativeWorks will supply ${clientName || "Client"} with both final deliverables as well as any source files used to produce them. Projects or tasks may be billed hourly or quoted at a fixed price if arrangements are made in advance.

2. BILLING AND PAYMENT TERMS:
For hourly billed services, project fees will be applied according to work hours spent on each assigned project. The number of hours and applicable rates will be specified in invoices.

For fixed price projects, the total project cost and payment schedule will be agreed upon in writing prior to commencement of work.

CreativeWorks will invoice ${clientName || "Client"} monthly for hourly services, or at the dates agreed upon for fixed price projects. Payment is due within 30 days from the date the invoice is generated.

${clientName || "Client"} agrees that standard turnaround time is no less than 48 hours. Rush requests requiring delivery in less than 48 hours may incur additional charges. CreativeWorks will communicate turnaround times and any additional costs for rush orders prior to starting work.

IN WITNESS WHEREOF, CreativeWorks and ${clientName || "Client"} agree to these Terms of Service.
`;

    return (
        <StyledContainer component="main">
            <WideStyledCard>
                <StyledCardContent component="form">
                    <Typography variant="h4" component="h1" gutterBottom>
                        Terms and Conditions
                    </Typography>

                    <Typography variant="body1" color="text.secondary" paragraph align="center">
                        Please review and accept the terms and conditions to continue with registration.
                    </Typography>

                    <AgreementContainer elevation={1}>
                        <AgreementText variant="body2" component="pre">
                            {agreementText}
                        </AgreementText>
                    </AgreementContainer>

                    <FormControlLabel
                        control={
                            <Checkbox 
                                checked={hasAgreed} 
                                onChange={(e) => setHasAgreed(e.target.checked)} 
                                color="primary" 
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I have read and agree to the terms and conditions outlined above
                            </Typography>
                        }
                    />

                    <NavigationButtons>
                        <Button variant="outlined" onClick={newRegistrationStep2} size="large">
                            Back
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleRegister} 
                            size="large" 
                            disabled={!hasAgreed}
                        >
                            Complete Registration
                        </Button>
                    </NavigationButtons>
                </StyledCardContent>
            </WideStyledCard>
            <Copyright />
        </StyledContainer>
    );
};

export default RegisterStep3;
